import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaImage, FaPlus, FaTrashAlt } from 'react-icons/fa';

const API_BASE_URL = 'https://admin.theporch.in/api';
const ITEMS_PER_PAGE = 8;

// ------------------------------------------------------------------
// --- Sub-Component: Item Form Modal (Handles Add/Edit/Delete) ---
// ------------------------------------------------------------------
const ItemFormModal = ({ item, categories, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    price: item?.price || 0,
    category: item?.category || categories[0] || '',
    description: item?.description || '',
    image_data: null, 
    current_image_url: item?.image_url || '', 
    id: item?.id || null, 
  });

  const [imagePreview, setImagePreview] = useState(item?.image_url || '');
  const [fileError, setFileError] = useState('');

  // --- FIX: Prevent background scrolling when modal is open ---
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const isEditMode = !!item;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024 * 2) { 
        setFileError('Image file must be less than 2MB for direct DB storage.');
        e.target.value = null; 
        return;
      }
      setFileError('');

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image_data: reader.result })); 
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, image_data: 'IMAGE_TO_BE_REMOVED' }));
      setImagePreview(''); 
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fileError) return;
    onSave(formData, isEditMode);
  };
  
  const handleDeleteClick = () => {
      if (window.confirm(`Are you sure you want to delete "${item.title}"? This action cannot be undone.`)) {
          onDelete(item.id);
      }
  };

  // UPDATED STYLES FOR FULL COVERAGE
  const modalStyle = {
    position: 'fixed', 
    top: 0, 
    left: 0, 
    width: '100vw', // Use viewport width
    height: '100vh', // Use viewport height
    backgroundColor: 'rgba(0, 0, 0, 0.75)', 
    display: 'flex', 
    justifyContent: 'center',
    alignItems: 'center', 
    zIndex: 9999, // Extremely high z-index to stay on top of everything
    backdropFilter: 'blur(5px)',
    overflowY: 'auto' // Allow the modal itself to scroll if the form is long
  };
  
  const contentStyle = {
    backgroundColor: 'white', 
    padding: '30px', 
    borderRadius: '12px', 
    width: '90%',
    maxWidth: '600px', 
    margin: '20px auto', // Adds margin when form is tall
    position: 'relative',
    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
    zIndex: 10000
  };

  const inputStyle = { width: '100%', padding: '10px', margin: '5px 0 15px', border: '1px solid #ccc', borderRadius: '6px', boxSizing: 'border-box' };
  
  const gridRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr', 
    gap: '15px'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <button 
          onClick={onClose} 
          style={{ position: 'absolute', top: '15px', right: '15px', background: '#f0f0f0', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontWeight: 'bold' }}>
         &times;
        </button>
        <h2 style={{marginTop: 0, marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px', color: '#333'}}>{isEditMode ? 'Edit Menu Item' : 'Add New Item'}</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={gridRowStyle}>
            <div>
              <label style={{fontWeight: 'bold', fontSize: '0.9rem'}}>Title:</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} style={inputStyle} required />
            </div>
            <div>
              <label style={{fontWeight: 'bold', fontSize: '0.9rem'}}>Price (Rs.):</label>
              <input type="number" name="price" value={formData.price} onChange={handleChange} style={inputStyle} required />
            </div>
          </div>

          <div style={gridRowStyle}>
            <div>
              <label style={{fontWeight: 'bold', fontSize: '0.9rem'}}>Category:</label>
              <select name="category" value={formData.category} onChange={handleChange} style={inputStyle} required>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
                <label style={{fontWeight: 'bold', fontSize: '0.9rem'}}>Image Upload:</label>
                <input 
                  type="file" 
                  name="file_upload" 
                  accept="image/*"
                  onChange={handleFileChange} 
                  style={{ ...inputStyle, padding: '7px' }}
                />
            </div>
          </div>

          <label style={{fontWeight: 'bold', fontSize: '0.9rem'}}>Description:</label>
          <textarea name="description" value={formData.description} onChange={handleChange} style={{ ...inputStyle, height: '80px', resize: 'vertical' }} />

          {fileError && <p style={{ color: 'red', fontSize: '0.8em', margin: '0 0 10px' }}>{fileError}</p>}
          
          {imagePreview && (
            <div style={{ marginBottom: '15px', textAlign: 'center' }}>
              <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '6px', border: '1px solid #eee', objectFit: 'contain' }} />
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <button type="submit" style={{ padding: '12px 25px', backgroundColor: '#8B4513', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                    {isEditMode ? 'Update Item' : 'Add Item'}
                </button>
                
                {isEditMode && (
                    <button 
                        type="button" 
                        onClick={handleDeleteClick} 
                        style={{ padding: '12px 15px', backgroundColor: '#DC3545', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <FaTrashAlt /> Delete
                    </button>
                )}
            </div>
        </form>
      </div>
    </div>
  );
};


// ------------------------------------------------------------------
// --- Main Component: MenuManager ---
// ------------------------------------------------------------------
const MenuManager = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [visibleItemCount, setVisibleItemCount] = useState(ITEMS_PER_PAGE); 
  const [imageErrorIds, setImageErrorIds] = useState({});

  const handleImageError = (itemId) => {
      setImageErrorIds(prev => ({ ...prev, [itemId]: true }));
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/menu_categories`);
      const fetchedCategories = response.data;
      setCategories(fetchedCategories);
      if (fetchedCategories.length > 0 && !selectedCategory) {
        setSelectedCategory(fetchedCategories[0]); 
      }
    } catch (err) {
      setError("Failed to fetch categories.");
      console.error("Fetch categories error:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []); 

  const fetchItems = async (category) => {
    if (!category) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/menu_items`, {
        params: { category: category }
      });
      setItems(response.data);
      setVisibleItemCount(ITEMS_PER_PAGE); 
      setImageErrorIds({}); 
    } catch (err) {
      setError(`Failed to fetch items for ${category}.`);
      console.error("Fetch items error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(selectedCategory);
  }, [selectedCategory]);

  const handleSave = async (formData, isEditMode) => {
    setLoading(true);
    setError(null);
    try {
        const { image_data, current_image_url, ...itemData } = formData;
        
        let finalImageData = current_image_url;
        if (image_data === 'IMAGE_TO_BE_REMOVED') {
            finalImageData = null;
        } else if (image_data) {
            finalImageData = image_data;
        }

        const payload = { ...itemData, image_url: finalImageData };
        
        if (isEditMode) {
            await axios.put(`${API_BASE_URL}/menu_items/${itemData.id}`, payload);
        } else {
            await axios.post(`${API_BASE_URL}/menu_items`, payload);
        }
        
        await fetchItems(selectedCategory);
        await fetchCategories(); 

        setIsModalOpen(false);
        setEditingItem(null);
    } catch (err) {
      setError(`Failed to ${isEditMode ? 'update' : 'add'} item.`);
    } finally {
        setLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
      setLoading(true);
      setError(null);
      try {
          await axios.delete(`${API_BASE_URL}/menu_items/${itemId}`);
          setIsModalOpen(false);
          setEditingItem(null);
          await fetchItems(selectedCategory);
      } catch (err) {
          setError('Failed to delete item.');
      } finally {
          setLoading(false);
      }
  };

  const openAddModal = () => {
    setEditingItem(null); 
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item); 
    setIsModalOpen(true);
  };
  
  const handleShowMore = () => {
    setVisibleItemCount(prevCount => prevCount + ITEMS_PER_PAGE);
  };

  // Main UI Styling
  const containerStyle = { 
    fontFamily: 'Arial, sans-serif', 
    padding: '20px', 
    width: '100%', 
    minHeight: '100vh',
    margin: '0 auto', 
    backgroundColor: '#f8f4f0', 
    boxSizing: 'border-box',
    position: 'relative' // Keeps everything in context
  };

  const navStyle = { display: 'flex', flexWrap: 'wrap', gap: '10px', paddingBottom: '10px', marginBottom: '20px' };
  const categoryButtonStyle = (cat) => ({
    padding: '8px 15px', cursor: 'pointer', border: '1px solid #8B4513', borderRadius: '5px',
    backgroundColor: selectedCategory === cat ? '#8B4513' : 'white',
    color: selectedCategory === cat ? 'white' : '#8B4513',
    fontWeight: selectedCategory === cat ? 'bold' : 'normal',
    transition: 'all 0.2s'
  });
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' };
  const itemInfoStyle = { flex: 1, padding: '0 10px' };
  const itemPriceStyle = { padding: '8px 15px', borderRadius: '20px', backgroundColor: '#D2B48C', color: '#4B371C', fontWeight: 'bold', whiteSpace: 'nowrap' };
  const itemImageContainerStyle = { width: '60px', height: '60px', borderRadius: '50%', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #D2B48C', backgroundColor: '#f9f9f9' };
  const PlaceholderIcon = () => (<FaImage size={30} color="#ccc" />);
  const showMoreButtonStyle = { display: 'block', width: '100%', padding: '12px 20px', marginTop: '20px', backgroundColor: '#A0522D', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1em', fontWeight: 'bold' }
  
  const itemsToDisplay = items.slice(0, visibleItemCount);
  const hasMore = items.length > visibleItemCount;

  return (
    <div style={containerStyle}>
      <style>{`
        .responsive-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          border: 1px solid #e0e0e0;
          padding: 10px;
          background-color: white;
          border-radius: 10px;
        }
        .responsive-item-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 10px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background-color 0.2s;
          background-color: white;
        }
        .responsive-item-card:hover {
          background-color: #fffaf5;
        }
        @media (max-width: 768px) {
          .responsive-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {error && <div style={{ color: 'red', marginBottom: '15px', padding: '10px', border: '1px solid red', backgroundColor: '#fee' }}>Error: {error}</div>}

      <div style={headerStyle}>
        <h1>Menu Management</h1>
        <button 
          onClick={openAddModal} 
          style={{ padding: '10px 20px', backgroundColor: '#3CB371', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}
        >
          <FaPlus /> Add Item
        </button>
      </div>

      <nav style={navStyle}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setSelectedCategory(cat)} style={categoryButtonStyle(cat)}>
            {cat}
          </button>
        ))}
      </nav>

      <h2>Items in {selectedCategory || '...'}</h2>

      {loading && items.length === 0 ? (
        <p>Loading items...</p>
      ) : (
        <>
          <div className="responsive-grid">
            {itemsToDisplay.length === 0 ? (
              <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '20px'}}>
                <p>No items found in this category. Click "➕ Add Item" to create one.</p>
              </div>
            ) : (
              itemsToDisplay.map(item => (
                  <div key={item.id} className="responsive-item-card" onClick={() => openEditModal(item)}>
                      <div style={itemImageContainerStyle}>
                          {imageErrorIds[item.id] || !item.image_url ? (
                              <PlaceholderIcon />
                          ) : (
                              <img src={item.image_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => handleImageError(item.id)} />
                          )}
                      </div>
                      <div style={itemInfoStyle}>
                          <h3 style={{ margin: '0', fontSize: '1.1em', color: '#8B4513' }}>{item.title}</h3>
                          <p style={{ margin: '3px 0 0', fontSize: '0.9em', color: '#555' }}>
                              {item.description ? `${item.description.substring(0, 70)}${item.description.length > 70 ? '...' : ''}` : 'No description.'}
                          </p>
                      </div>
                      <div style={itemPriceStyle}>Rs. {parseFloat(item.price).toFixed(2)}</div>
                  </div>
              ))
            )}
          </div>
          {hasMore && <button onClick={handleShowMore} style={showMoreButtonStyle}>Show More ({items.length - visibleItemCount} remaining)</button>}
        </>
      )}

      {isModalOpen && (
        <ItemFormModal
          item={editingItem}
          categories={categories}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default MenuManager;